/**
 * npeContractsApi — RTK Query slice cho /purchase/npe-contracts/*.
 * Tag types: 'NPEContract' (list + detail).
 *
 * Sprint 4 (SOP §7.5) — endpoints:
 *   GET    /purchase/npe-contracts/list?q=&cp_code=&contract_date_from=&contract_date_to=&page=&page_size=
 *   POST   /purchase/npe-contracts/create
 *   GET    /purchase/npe-contracts/{id}                  // id = consignment_p_id
 *   PUT    /purchase/npe-contracts/{id}
 *   DELETE /purchase/npe-contracts/{id}
 *   POST   /purchase/npe-contracts/{id}/copy
 *   GET    /purchase/npe-contracts/{id}/print            // placeholder Sprint 6
 */
import { baseApi } from './baseApi'
import type {
  DataResponse,
  LocalPPrintResult,
  MessageResponse,
  NPEContractCreateIn,
  NPEContractListQuery,
  NPEContractOut,
  NPEContractUpdateIn,
  PageResponse,
} from '@/types/api'

export const npeContractsApi = baseApi.injectEndpoints({
  endpoints: (b) => ({
    listNPEContracts: b.query<PageResponse<NPEContractOut>, NPEContractListQuery | void>({
      query: (params) => ({
        url: '/purchase/npe-contracts/list',
        params: params ?? {},
      }),
      providesTags: (res) =>
        res
          ? [
              ...res.items.map((it) => ({
                type: 'NPEContract' as const,
                id: it.consignment_p_id,
              })),
              { type: 'NPEContract' as const, id: 'LIST' },
            ]
          : [{ type: 'NPEContract' as const, id: 'LIST' }],
    }),
    getNPEContract: b.query<DataResponse<NPEContractOut>, number>({
      query: (id) => `/purchase/npe-contracts/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'NPEContract', id }],
    }),
    createNPEContract: b.mutation<DataResponse<NPEContractOut>, NPEContractCreateIn>({
      query: (body) => ({
        url: '/purchase/npe-contracts/create',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'NPEContract', id: 'LIST' }],
    }),
    updateNPEContract: b.mutation<
      DataResponse<NPEContractOut>,
      { id: number; body: NPEContractUpdateIn }
    >({
      query: ({ id, body }) => ({
        url: `/purchase/npe-contracts/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'NPEContract', id: arg.id },
        { type: 'NPEContract', id: 'LIST' },
      ],
    }),
    deleteNPEContract: b.mutation<MessageResponse, number>({
      query: (id) => ({
        url: `/purchase/npe-contracts/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, id) => [
        { type: 'NPEContract', id },
        { type: 'NPEContract', id: 'LIST' },
      ],
    }),
    copyNPEContract: b.mutation<DataResponse<NPEContractOut>, number>({
      query: (id) => ({
        url: `/purchase/npe-contracts/${id}/copy`,
        method: 'POST',
      }),
      invalidatesTags: [{ type: 'NPEContract', id: 'LIST' }],
    }),
    /** Print PDF (placeholder Sprint 6). */
    printNPEContract: b.query<DataResponse<LocalPPrintResult>, number>({
      query: (id) => `/purchase/npe-contracts/${id}/print`,
    }),
  }),
})

export const {
  useListNPEContractsQuery,
  useGetNPEContractQuery,
  useCreateNPEContractMutation,
  useUpdateNPEContractMutation,
  useDeleteNPEContractMutation,
  useCopyNPEContractMutation,
  useLazyPrintNPEContractQuery,
} = npeContractsApi
